import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import usePageMetadata from '../utils/usePageMetadata';
import './Auth.css';

const Signup = () => {
  usePageMetadata({
    title: 'Sign Up | The Ad Shop',
    description:
      'Create your free Ad Shop account to start tracking GA4, AdSense, and Search Console data in one unified dashboard. No credit card required.',
    canonicalUrl: 'https://theadshop.co/signup',
    meta: [
      { property: 'og:title', content: 'Sign Up | The Ad Shop' },
      {
        property: 'og:description',
        content:
          'Create your free account to access unified analytics for GA4, AdSense, and Search Console. Start tracking your data today.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: 'https://theadshop.co/signup' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Sign Up | The Ad Shop' },
      {
        name: 'twitter:description',
        content:
          'Create your free account to access unified analytics for GA4, AdSense, and Search Console.',
      },
    ],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Sign Up for The Ad Shop',
      url: 'https://theadshop.co/signup',
      description:
        'Create your free Ad Shop account to access unified analytics dashboard for publishers.',
      isPartOf: {
        '@type': 'WebSite',
        name: 'The Ad Shop',
        url: 'https://theadshop.co/',
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://theadshop.co/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Sign Up',
            item: 'https://theadshop.co/signup',
          },
        ],
      },
    },
  });
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    websiteUrl: '',
    newsletterSubscribed: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    // Client-side validation
    if (formData.password !== formData.confirmPassword) {
      setErrors({ confirmPassword: 'Passwords do not match' });
      setLoading(false);
      return;
    }

    try {
      const { confirmPassword, ...signupData } = formData;
      const response = await axios.post('/api/auth/register', signupData);

      if (response.data.requiresApproval) {
        navigate('/waitlist', { state: { email: formData.email } });
        return;
      }

      if (response.data.requiresVerification) {
        setErrors({ 
          general: 'Please check your email to verify your account before logging in. The verification link will expire in 24 hours.' 
        });
        return;
      }

      login(response.data.user, response.data.token);
      navigate('/dashboard');
    } catch (error) {
      if (error.response?.data?.errors) {
        const formattedErrors = {};
        error.response.data.errors.forEach(err => {
          formattedErrors[err.param] = err.msg;
        });
        setErrors(formattedErrors);
      } else {
        setErrors({ general: error.response?.data?.message || 'Signup failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const redirectToGoogleOAuth = (flowType) => {
    const state = { type: flowType };
    const redirectUri = `${window.location.origin}/auth/google-oauth-callback`;

    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'email profile',
      access_type: 'offline',
      prompt: 'select_account', // Removed 'consent' - only show consent if permissions not already granted
      include_granted_scopes: 'true',
      state: JSON.stringify(state)
    });

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    window.location.href = authUrl;
  };

  const handleGoogleSignup = async () => {
    try {
      setLoading(true);
      
      // Clear any existing token before starting new OAuth flow
      // This ensures we don't accidentally use a cached token from a different account
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];

      // Always use redirect flow for reliability across browsers/devices
      redirectToGoogleOAuth('signup');
    } catch (error) {
      console.error('Google OAuth error:', error);
      setErrors({ general: 'Google authentication failed. Please try again or use email signup.' });
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="auth-container">
        <div className="auth-card">
        <div className="auth-header">
            <img src="/images/the-ad-shop-logo.svg" alt="The Ad Shop" className="auth-logo" />
          <h1>Create Account</h1>
          <p>Get started with your reporting dashboard</p>
        </div>

        {errors.general && (
          <div className="error-message">{errors.general}</div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={errors.firstName ? 'error' : ''}
                required
              />
              {errors.firstName && <span className="field-error">{errors.firstName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={errors.lastName ? 'error' : ''}
                required
              />
              {errors.lastName && <span className="field-error">{errors.lastName}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              required
            />
            {errors.email && <span className="field-error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="websiteUrl">Website URL</label>
            <input
              type="url"
              id="websiteUrl"
              name="websiteUrl"
              value={formData.websiteUrl}
              onChange={handleChange}
              placeholder="https://example.com"
              className={errors.websiteUrl ? 'error' : ''}
              required
            />
            {errors.websiteUrl && <span className="field-error">{errors.websiteUrl}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'error' : ''}
                required
              />
              {errors.password && <span className="field-error">{errors.password}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={errors.confirmPassword ? 'error' : ''}
                required
              />
              {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
            </div>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="newsletterSubscribed"
                checked={formData.newsletterSubscribed}
                onChange={(e) => setFormData({ ...formData, newsletterSubscribed: e.target.checked })}
              />
              <span>Subscribe to our newsletter for publisher-facing ad industry news and revenue optimization tips</span>
            </label>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-divider">
          <span>OR</span>
        </div>

        <button onClick={handleGoogleSignup} className="btn btn-google" disabled={loading}>
          {loading ? (
            <>
              <div className="spinner-small"></div>
              Connecting...
            </>
          ) : (
            <>
              <svg className="google-icon" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>

        <div className="auth-footer">
          <p>
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Signup;

